import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, Button, DialogActions, Grid } from "@mui/material";
import Typography from "@mui/material/Typography/Typography";
import { yellow } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { Dispatch, useContext, useState } from "react";
import { routes } from "schemas/routes";
import { LIGHT_GREY } from "styles/oneOffColor";

import { Template } from "features/Influencer/ContactList/schema";
import TemplateBody from "features/Influencer/Templates/SharedComponents/TemplateBody";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";

interface Props {
  editTemplate: Template;
  setEditTemplate: Dispatch<React.SetStateAction<Template>>;
  followUpTemplate?: Template;
  setFollowUpTemplate?: Dispatch<React.SetStateAction<Template>>;
  handleBack: () => void;
  handleNext: () => void;
  allowChangeTitle?: boolean;
  setIsDirty: Dispatch<React.SetStateAction<boolean>>;
}
const ReviewBentoAiTemplate = ({
  editTemplate,
  setEditTemplate,
  handleBack,
  handleNext,
  followUpTemplate,
  setFollowUpTemplate,
  setIsDirty,
  allowChangeTitle = false,
}: Props) => {
  const { setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);

  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const isVaOnboarding = isPage(routes.templatesSelfServeOnboarding);

  const handleSaveTemplate = async () => {
    setIsDirty(false);
    try {
      setSaveLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${editTemplate.id}`,
        "PUT",
        {},
        {
          ...editTemplate,
          validateBulkSendVariables: Boolean(isVaOnboarding),
        },
      );
      setEditTemplate(res.template);

      if (followUpTemplate && followUpTemplate?.id) {
        const { template: followup } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/templates/${followUpTemplate?.id}`,
          "PUT",
          {},
          { ...followUpTemplate },
        );
        if (setFollowUpTemplate) setFollowUpTemplate(followup);
      }
      if (handleNext) {
        handleNext();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Box sx={isPage(routes.onboardingVaSelfServe) ? { p: 3 } : {}}>
      <Typography variant="h4" gutterBottom>
        Review Your Template
      </Typography>
      <>
        <Box component="ul" sx={{ pl: 0 }}>
          <li style={{ listStyleType: "none" }}>
            <Typography>
              👀 Review the tone, details, and add anything you'd like before
              saving.
            </Typography>
          </li>
          <li style={{ listStyleType: "none" }}>
            <Typography>
              <Box
                component="i"
                className="fa-solid fa-sparkles"
                sx={{ pr: 1, color: yellow[700] }}
              />
              Keep{" "}
              <Box component="span" sx={{ color: "purple.dark" }}>
                {"{Brand Name}"}
              </Box>{" "}
              and{" "}
              <Box component="span" sx={{ color: "purple.dark" }}>
                {"{First Name}"}
              </Box>{" "}
              as placeholders—this ensures each email is personalized for the
              brand and recipient.
            </Typography>
          </li>
          {isVaOnboarding ? (
            <li style={{ listStyleType: "none" }}>
              <Typography>
                🚨 Make sure your templates <strong>do not contain</strong> any
                other variables beside{" "}
                <Box component="span" sx={{ color: "purple.dark" }}>
                  {"{Brand Name}"}
                </Box>{" "}
                and{" "}
                <Box component="span" sx={{ color: "purple.dark" }}>
                  {"{First Name}"}
                </Box>
                .
              </Typography>
            </li>
          ) : (
            <></>
          )}
        </Box>
      </>
      <TemplateBody
        editTemplate={editTemplate}
        setEditTemplate={setEditTemplate}
        isNew={true}
        allowChangeTitle={allowChangeTitle}
        minimalist
        displayError={true}
        setIsDirty={setIsDirty}
      />

      {followUpTemplate && setFollowUpTemplate && (
        <TemplateBody
          editTemplate={followUpTemplate}
          setEditTemplate={setFollowUpTemplate}
          isNew={true}
          allowChangeTitle={allowChangeTitle}
          minimalist
          displayError={true}
          setIsDirty={setIsDirty}
        />
      )}

      <DialogActions>
        <Grid container columnGap={1} justifyContent={"center"}>
          <Button
            sx={{ backgroundColor: LIGHT_GREY, px: 3, py: 1 }}
            disableElevation
            onClick={handleBack}
          >
            <Box
              component={"i"}
              className="fa-regular fa-circle-arrow-left"
              sx={{ mr: 1 }}
            />{" "}
            Back
          </Button>
          <LoadingButton
            disableElevation
            variant="contained"
            type="submit"
            onClick={handleSaveTemplate}
            loading={saveLoading}
            sx={{ px: 3, py: 1 }}
          >
            Next{" "}
          </LoadingButton>
        </Grid>
      </DialogActions>
    </Box>
  );
};

export default ReviewBentoAiTemplate;
